/**
 * Okta Connect Wizard for /admin/saas-spend.
 *
 * Same UX pattern as PipelineConnectorWizard: enter domain + SSWS token →
 * Test → Save → "Sync now" button pulls the full app catalog.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Plug, CheckCircle2, AlertTriangle, RefreshCw, Trash2, ShieldCheck, ExternalLink } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

function ProbeBadge({ state }) {
  if (state === 'idle') return null;
  if (state === 'testing') return <Badge className="bg-slate-700/60 text-slate-200">Testing…</Badge>;
  if (state === 'ok') return <Badge className="bg-emerald-500/15 text-emerald-300 border border-emerald-500/30"><CheckCircle2 className="w-3 h-3 inline mr-1" /> Connected</Badge>;
  return <Badge className="bg-rose-500/15 text-rose-300 border border-rose-500/30"><AlertTriangle className="w-3 h-3 inline mr-1" /> Failed</Badge>;
}

export default function OktaConnectWizard({ onSyncComplete }) {
  const [config, setConfig] = useState(null);
  const [form, setForm] = useState({ okta_domain: '', api_token: '' });
  const [probe, setProbe] = useState({ state: 'idle', msg: null });
  const [syncBusy, setSyncBusy] = useState(false);
  const [syncResult, setSyncResult] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const r = await fetch(`${API}/api/okta-discovery/config`, { headers: { Authorization: `Bearer ${token}` } });
      const d = await r.json();
      setConfig(d.connected ? d : null);
    } catch { /* ignore */ }
  }, []);
  useEffect(() => { refresh(); }, [refresh]);

  const callApi = async (path, method, body) => {
    const token = localStorage.getItem('token');
    const r = await fetch(`${API}${path}`, {
      method, headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: body ? JSON.stringify(body) : undefined,
    });
    const text = await r.text();
    let json = null;
    try { json = JSON.parse(text); } catch { /* non-json */ }
    return { ok: r.ok, status: r.status, body: json || text };
  };

  const test = async () => {
    setProbe({ state: 'testing', msg: null });
    const r = await callApi('/api/okta-discovery/test', 'POST', form);
    if (r.ok && r.body.ok) setProbe({ state: 'ok', msg: r.body.sample_app_name ? `Reachable · sample: ${r.body.sample_app_name}` : 'Reachable' });
    else setProbe({ state: 'fail', msg: r.body?.error || r.body?.detail || `HTTP ${r.status}` });
  };

  const connect = async () => {
    setProbe({ state: 'testing', msg: null });
    const r = await callApi('/api/okta-discovery/connect', 'POST', form);
    if (r.ok) {
      setProbe({ state: 'ok', msg: 'Connected' });
      setForm({ okta_domain: '', api_token: '' });
      refresh();
    } else setProbe({ state: 'fail', msg: r.body?.detail || `HTTP ${r.status}` });
  };

  const sync = async () => {
    setSyncBusy(true);
    setSyncResult(null);
    const r = await callApi('/api/okta-discovery/sync', 'POST');
    setSyncBusy(false);
    if (r.ok) {
      setSyncResult(r.body);
      onSyncComplete?.(r.body);
      refresh();
    } else setSyncResult({ error: r.body?.detail || `HTTP ${r.status}` });
  };

  const disconnect = async () => {
    if (!window.confirm('Disconnect Okta? Encrypted token will be deleted. Imported SaaS subscriptions are kept.')) return;
    await callApi('/api/okta-discovery/config', 'DELETE');
    refresh();
    setSyncResult(null);
  };

  return (
    <Card className="bg-slate-950 border-cyan-400/30" data-testid="okta-connect-wizard">
      <CardHeader>
        <CardTitle className="text-base text-white flex items-center gap-2">
          <Plug className="w-4 h-4 text-cyan-300" />
          Connect Okta — auto-discover every SaaS app
        </CardTitle>
        <p className="text-sm text-slate-200 mt-1">CARTIE pulls your Okta app catalog + cross-references it against existing subscriptions for overlap. Token encrypted with AES-128 (same vault as cloud-provider creds).</p>
      </CardHeader>
      <CardContent className="space-y-4">
        {config ? (
          <div className="rounded-md bg-emerald-500/5 border border-emerald-500/20 p-3">
            <div className="flex items-center justify-between gap-3">
              <div className="text-sm">
                <div className="flex items-center gap-2 mb-0.5">
                  <ShieldCheck className="w-4 h-4 text-emerald-400" />
                  <span className="text-white font-bold">Connected</span>
                  <code className="text-xs text-cyan-300 font-mono">{config.okta_domain}</code>
                </div>
                {config.last_sync_at && (
                  <div className="text-xs text-slate-400">
                    Last sync: {new Date(config.last_sync_at).toLocaleString()} · created {config.last_sync_created} · updated {config.last_sync_updated}
                  </div>
                )}
              </div>
              <div className="flex items-center gap-2">
                <Button size="sm" onClick={sync} disabled={syncBusy} className="bg-emerald-700 hover:bg-emerald-600 text-white h-8 text-xs" data-testid="okta-sync-btn">
                  {syncBusy ? 'Syncing…' : <><RefreshCw className="w-3 h-3 mr-1" /> Sync now</>}
                </Button>
                <Button size="sm" variant="ghost" onClick={disconnect} className="text-rose-400 hover:text-rose-300 h-8 px-2">
                  <Trash2 className="w-3 h-3" />
                </Button>
              </div>
            </div>
            {syncResult && (
              <div className="mt-3 text-xs">
                {syncResult.error ? (
                  <div className="text-rose-300">{syncResult.error}</div>
                ) : (
                  <div className="text-emerald-300">
                    ✓ {syncResult.total_apps_discovered} apps discovered · {syncResult.created} created · {syncResult.updated} updated
                  </div>
                )}
              </div>
            )}
          </div>
        ) : (
          <div className="rounded-md bg-slate-950 border border-slate-700 p-3" data-testid="okta-form">
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs font-bold text-white">Okta domain + SSWS token</div>
              <ProbeBadge state={probe.state} />
            </div>
            <input
              type="text" placeholder="https://yourorg.okta.com" value={form.okta_domain}
              onChange={(e) => setForm({ ...form, okta_domain: e.target.value })}
              className="w-full mb-2 bg-slate-900 border border-slate-700 rounded px-2 py-1 text-xs text-white"
              data-testid="okta-domain-input"
            />
            <input
              type="password" placeholder="Okta SSWS API token (00abcd…)" value={form.api_token}
              onChange={(e) => setForm({ ...form, api_token: e.target.value })}
              className="w-full mb-2 bg-slate-900 border border-slate-700 rounded px-2 py-1 text-xs text-white"
              data-testid="okta-token-input"
            />
            <div className="flex gap-2 mt-1">
              <Button size="sm" variant="outline" onClick={test} disabled={!form.okta_domain || !form.api_token} className="text-xs h-7" data-testid="okta-test-btn">Test</Button>
              <Button size="sm" onClick={connect} disabled={!form.okta_domain || !form.api_token} className="bg-emerald-700 hover:bg-emerald-600 text-white text-xs h-7" data-testid="okta-connect-btn">Save</Button>
              <a href="https://developer.okta.com/docs/guides/create-an-api-token/main/" target="_blank" rel="noreferrer" className="ml-auto text-xs text-slate-400 hover:text-cyan-300 flex items-center gap-1">
                How to get a token <ExternalLink className="w-3 h-3" />
              </a>
            </div>
            {probe.msg && <div className={`mt-2 text-xs ${probe.state === 'ok' ? 'text-emerald-300' : 'text-rose-300'}`}>{probe.msg}</div>}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
